import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';

import RejectCheckBottomSheet from './RejectCheckBottomSheet';
import Icon from '../../../../Assets/Icon/CustomIcon';
import {
  CustomCalendar,
  TimePicker,
  getTimePickerValue,
} from './DateTimePicker';
import { CustomTextAreaBox, CompactInputCell, InfoCell } from './Inputs';

dayjs.locale('ko');

const MAX_WORK_HOURS = 9;

const DATE_FIELDS = [
  {
    label: '시작 일자',
    storeKey: 'workStartDate',
    pickerType: 'startDate',
    row: 'date',
  },
  {
    label: '종료 일자',
    storeKey: 'workEndDate',
    pickerType: 'endDate',
    row: 'date',
  },
  {
    label: '시작 시간',
    storeKey: 'workStartDate',
    pickerType: 'startTime',
    row: 'time',
  },
  {
    label: '종료 시간',
    storeKey: 'workEndDate',
    pickerType: 'endTime',
    row: 'time',
  },
];

const COMPANY_FIELDS = [
  { label: '업체명', key: 'compNm', type: 'text' },
  { label: '책임자', key: 'mgrNm', type: 'text' },
  { label: '전화번호', key: 'mgrTel', type: 'tel' },
];

const WORK_FIELDS = [
  {
    label: '작업장소',
    key: 'workPlace',
    icon: Icon.Location,
    placeholder: '작업 장소를 구체적으로 입력하세요',
  },
  {
    label: '작업내용',
    key: 'workDetail',
    icon: Icon.Description,
    placeholder: '작업 내용을 입력하세요',
  },
];

function convertTo24Hour(timeString) {
  const [hourStr, minute, period] = timeString.split(/[: ]/);
  let hour = parseInt(hourStr, 10);
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${minute}`;
}

function toMinutes(hhmm) {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

/** 반복 일자와 무관, 시계만 기준으로 시작 시각에서 정방향 경과 분(자정 넘김 포함) */
function forwardWorkMinutes(startHm, endHm) {
  const s = toMinutes(startHm);
  const e = toMinutes(endHm);
  if (e === s) return 0;
  if (e > s) return e - s;
  return e + 1440 - s;
}

const getOnlyNumber = (value) => value.replace(/[^0-9]/g, '');

const formatPhoneNumber = (value) => {
  const num = getOnlyNumber(value);
  if (num.length <= 3) return num;
  if (num.length <= 7) return `${num.slice(0, 3)}-${num.slice(3)}`;
  if (num.length <= 11)
    return `${num.slice(0, 3)}-${num.slice(3, 7)}-${num.slice(7)}`;
  return `${num.slice(0, 3)}-${num.slice(3, 7)}-${num.slice(7, 11)}`; // 11자리 초과 방지
};

export default function BasicInfoForm({ errors, handleChange, rowData2 }) {
  const dispatch = useDispatch();
  const { baseInfo } = useSelector((state) => state.permit);
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const bottomSheetState = useSelector((state) => state.bottomSheet);

  const [pickerVisible, setPickerVisible] = useState(null);
  const [workerInput, setWorkerInput] = useState('');

  const infoList = [
    { label: '소속', value: userInfo.depNm },
    { label: '직위', value: userInfo.rank },
    { label: '성명', value: userInfo.userNm },
  ];

  const currentWorkers = baseInfo.workList
    ? baseInfo.workList
        .split(',')
        .map((w) => w.trim())
        .filter(Boolean)
    : [];

  const handleAddWorker = () => {
    const name = workerInput.trim();
    if (!name) return;
    if (currentWorkers.includes(name)) {
      dispatch(
        showSnackbar({ message: '이미 등록된 이름입니다.', type: 'warning' })
      );
      return;
    }
    handleChange('workList', [...currentWorkers, name].join(', '));
    setWorkerInput('');
  };

  // 종료 날짜 최대값: 시작일 기준 최대 5일(+4일)
  const endDateMax = useMemo(() => {
    const startStr = baseInfo.workStartDate;
    if (!startStr) return null;
    const [startDate] = startStr.split(' ');
    return dayjs(startDate).add(4, 'day').format('YYYY-MM-DD');
  }, [baseInfo.workStartDate]);

  // 종료 시간 제약 표시 및 피커(relative 규칙). 일자 필드와는 별개.
  const endTimeConstraints = useMemo(() => {
    const startStr = baseInfo.workStartDate;
    if (!startStr) return { minTime: null, maxTime: null, relativePick: null };

    const startTime = startStr.split(' ')[1];
    if (!startTime) return { minTime: null, maxTime: null, relativePick: null };

    const base = dayjs(`2000-01-01 ${startTime}`);
    const startAbs = toMinutes(startTime);

    return {
      minTime: base.add(1, 'minute').format('HH:mm'),
      maxTime: base.add(MAX_WORK_HOURS, 'hour').format('HH:mm'),
      relativePick: {
        startAbsMinutes: startAbs,
        maxDurationMinutes: MAX_WORK_HOURS * 60,
      },
    };
  }, [baseInfo.workStartDate]);

  const handleDateChange = (date) => {
    const isStart = pickerVisible === 'startDate';
    const key = isStart ? 'workStartDate' : 'workEndDate';
    const defaultTime = isStart ? '09:00' : '18:00';
    const currentTime = baseInfo[key]?.split(' ')[1] || defaultTime;
    handleChange(key, `${date} ${currentTime}`);

    // 시작일 변경 시: 종료일이 새 시작일보다 이전이면 종료일을 시작일로 맞춤
    if (isStart) {
      const endDate = baseInfo.workEndDate?.split(' ')[0];
      const endTime = baseInfo.workEndDate?.split(' ')[1] || '18:00';
      if (endDate && endDate < date) {
        handleChange('workEndDate', `${date} ${endTime}`);
        dispatch(
          showSnackbar({
            message: '종료 일자가 시작 일자로 초기화되었습니다.',
            type: 'warning',
          })
        );
      }
    }

    setPickerVisible(isStart ? 'endDate' : 'startTime');
  };

  const handleTimeChange = (formatted) => {
    const isEnd = pickerVisible === 'endTime';
    const key = isEnd ? 'workEndDate' : 'workStartDate';
    const currentDate = baseInfo[key]?.split(' ')[0];
    const time24 = convertTo24Hour(formatted);

    if (isEnd) {
      const startTime = baseInfo.workStartDate?.split(' ')[1];
      if (startTime) {
        const fwd = forwardWorkMinutes(startTime, time24);
        if (fwd <= 0) {
          dispatch(
            showSnackbar({
              message: '종료 시간은 시작 시간 이후여야 합니다.',
              type: 'error',
            })
          );
          return;
        }
        if (fwd > MAX_WORK_HOURS * 60) {
          dispatch(
            showSnackbar({
              message: `작업 시간은 최대 ${MAX_WORK_HOURS}시간까지 설정 가능합니다.`,
              type: 'error',
            })
          );
          return;
        }
      }
    } else {
      const endTime = baseInfo.workEndDate?.split(' ')[1];
      const endDate = baseInfo.workEndDate?.split(' ')[0];
      if (endTime && endDate) {
        const fwd = forwardWorkMinutes(time24, endTime);
        const maxDur = MAX_WORK_HOURS * 60;
        if (fwd <= 0 || fwd > maxDur) {
          const resetTime =
            fwd > maxDur
              ? dayjs(`2000-01-01 ${time24}`)
                  .add(MAX_WORK_HOURS, 'hour')
                  .format('HH:mm')
              : dayjs(`2000-01-01 ${time24}`)
                  .add(1, 'minute')
                  .format('HH:mm');
          handleChange('workEndDate', `${endDate} ${resetTime}`);
          dispatch(
            showSnackbar({
              message: '시작 시간 변경으로 인해 종료 시간이 재설정되었습니다.',
              type: 'warning',
            })
          );
        }
      }
    }

    handleChange(key, `${currentDate} ${time24}`);
  };

  const getButtonLabel = ({ storeKey, row }) => {
    const raw = baseInfo[storeKey];
    if (row === 'date') return raw?.split(' ')[0] || '날짜 선택';
    return raw?.split(' ')[1] || '시간 선택';
  };

  return (
    <div className="font-body min-h-screen bg-slate-50">
      <div className="mx-auto max-w-2xl space-y-6 px-2 pb-40">
        <section className="relative overflow-hidden rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="relative z-10 flex items-center gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
              {userInfo.photo ? (
                <img
                  alt="User Profile"
                  className="ring-primary-container object-cover ring-4"
                  src={userInfo.photo}
                />
              ) : (
                <Icon.User />
              )}
            </div>
            <div className="flex-1">
              <span className="text-primary text-sm font-bold tracking-wider uppercase">
                {userInfo.posiNm}
              </span>
              <h2 className="text-xl leading-tight font-extrabold text-slate-900">
                {userInfo.userNm}{' '}
                {userInfo.rank && (
                  <span className="text-sm font-medium text-slate-400">
                    ({userInfo.rank})
                  </span>
                )}
              </h2>
              <p className="text-sm font-medium text-slate-500">
                {userInfo.depNm}
              </p>
            </div>
          </div>
        </section>

        <div className="space-y-4">
          <h3 className="px-1 text-sm font-bold tracking-widest text-slate-400 uppercase">
            작업개요 입력
          </h3>

          <div className="rounded-2xl border border-slate-200/50 bg-slate-100/50 px-5 py-3">
            <div className="flex items-center text-sm font-bold text-slate-400">
              <Icon.Assignment className="h-5 w-5" />
              <span>신청자 정보(자동입력)</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {infoList.map((info) => (
                <InfoCell
                  key={info.label}
                  label={info.label}
                  value={info.value}
                />
              ))}
            </div>
          </div>

          <div className="space-y-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <label className="mb-1 flex items-center gap-1 text-sm font-bold text-slate-500 uppercase">
              <Icon.Clock className="text-secondary h-4 w-4" />
              작업 일시
            </label>

            {['date', 'time'].map((row) => (
              <div key={row} className="grid grid-cols-2 gap-3">
                {DATE_FIELDS.filter((f) => f.row === row).map((field) => (
                  <div key={field.pickerType} className="flex flex-col gap-2">
                    <span className="text-[11px] font-medium text-slate-400">
                      {field.label}
                    </span>
                    <button
                      onClick={() =>
                        setPickerVisible((prev) =>
                          prev === field.pickerType ? null : field.pickerType
                        )
                      }
                      className={`rounded-xl border p-3 text-left text-sm ${
                        pickerVisible === field.pickerType
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200'
                      }`}
                    >
                      {getButtonLabel(field)}
                    </button>
                  </div>
                ))}
              </div>
            ))}

            <div className="mt-2">
              {(pickerVisible === 'startDate' ||
                pickerVisible === 'endDate') && (
                <CustomCalendar
                  value={
                    baseInfo[
                      pickerVisible === 'startDate'
                        ? 'workStartDate'
                        : 'workEndDate'
                    ]?.split(' ')[0]
                  }
                  minDate={
                    pickerVisible === 'endDate'
                      ? baseInfo.workStartDate?.split(' ')[0]
                      : null
                  }
                  maxDate={
                    pickerVisible === 'endDate' ? endDateMax : null
                  }
                  onChange={handleDateChange}
                />
              )}

              {(pickerVisible === 'startTime' ||
                pickerVisible === 'endTime') && (
                <div className="border-primary/10 rounded-2xl border bg-slate-50 p-4">
                  <TimePicker
                    value={getTimePickerValue(
                      baseInfo[
                        pickerVisible === 'startTime'
                          ? 'workStartDate'
                          : 'workEndDate'
                      ]
                    )}
                    onChange={handleTimeChange}
                    relativeEndPick={
                      pickerVisible === 'endTime'
                        ? endTimeConstraints.relativePick
                        : null
                    }
                  />

                  {pickerVisible === 'endTime' &&
                    (endTimeConstraints.minTime ||
                      endTimeConstraints.maxTime) && (
                      <p className="my-2 text-center text-sm text-slate-400">
                        {endTimeConstraints.minTime
                          ? `${endTimeConstraints.minTime} ~ `
                          : '~ '}
                        {endTimeConstraints.maxTime} 선택 가능
                        <span className="ml-1 text-slate-300">
                          (최대 {MAX_WORK_HOURS}시간)
                        </span>
                      </p>
                    )}

                  <button
                    onClick={() => setPickerVisible(null)}
                    className="mt-4 w-full rounded-xl bg-black py-3 text-sm font-bold text-white shadow-md"
                  >
                    설정 완료
                  </button>
                </div>
              )}
            </div>
          </div>

          {WORK_FIELDS.map((field) => (
            <CustomTextAreaBox
              key={field.key}
              label={field.label}
              icon={field.icon}
              iconColor="text-primary"
              value={baseInfo[field.key]}
              onChange={(v) => handleChange(field.key, v)}
              placeholder={field.placeholder}
              error={errors?.[field.key]}
            />
          ))}

          <div className="space-y-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <label className="flex items-center gap-1 text-sm font-bold text-slate-500 uppercase">
              <Icon.Corporate className="material-symbols-outlined text-primary h-4 w-4 text-sm" />
              업체정보 (선택)
            </label>
            <div className="space-y-2">
              {COMPANY_FIELDS.map((field) => (
                <CompactInputCell
                  key={field.key}
                  label={field.label}
                  type={field.type}
                  value={baseInfo[field.key] || ''}
                  onChange={(v) => {
                    if (field.type === 'tel') {
                      const formatted = formatPhoneNumber(v);
                      handleChange(field.key, formatted);
                    } else {
                      handleChange(field.key, v);
                    }
                  }}
                />
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <label className="mb-3 flex items-center gap-1 text-sm font-bold text-slate-500 uppercase">
              <Icon.Groups className="text-secondary h-4 w-4" />
              작업자 명단
            </label>

            <div className="mb-4 flex w-full items-center gap-2">
              <input
                type="text"
                value={workerInput}
                onChange={(e) => setWorkerInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && (e.preventDefault(), handleAddWorker())
                }
                className="focus:ring-primary/20 min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-base font-semibold outline-none focus:ring-2"
                placeholder="작업자 이름 입력"
              />
              <button
                onClick={handleAddWorker}
                className="shrink-0 rounded-xl bg-slate-900 px-5 py-2 text-sm font-bold whitespace-nowrap text-white active:scale-95"
              >
                추가
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {currentWorkers.map((worker, idx) => (
                <div
                  key={idx}
                  className="inline-flex min-h-8 items-center justify-center rounded-full border border-slate-200 bg-slate-100 py-1 pr-1 pl-2 shadow-sm"
                >
                  <span className="text-sm leading-none font-bold text-slate-700">
                    {worker}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      handleChange(
                        'workList',
                        currentWorkers.filter((_, i) => i !== idx).join(', ')
                      )
                    }
                    className="m-0 flex h-6 w-6 shrink-0 items-center justify-center p-0 leading-none text-slate-600 transition-colors hover:text-red-500"
                  >
                    <Icon.Cancel className="block h-4 w-4 shrink-0" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {bottomSheetState.bottom && bottomSheetState.type === 'rejecCheck' && (
        <RejectCheckBottomSheet returnDetail={rowData2?.returnReason} />
      )}
    </div>
  );
}
